import type { Group, Lesson } from '@/hooks/useDashboardData';
import { lessonStatusMap, getStatusMeta } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';

interface TodayLessonsProps {
  lessons: Lesson[];
  groups: Group[];
}

function getTodayStr() {
  return new Date().toISOString().slice(0, 10);
}

export default function TodayLessons({ lessons, groups }: TodayLessonsProps) {
  const today = getTodayStr();
  const todayLessons = lessons.filter((l) => l.date === today);

  return (
    <div className="space-y-4">
      {todayLessons.length === 0 && (
        <p className="rounded-lg bg-background-100 px-4 py-6 text-center text-sm text-foreground-500">
          Bugün için planlanmış ders bulunmuyor.
        </p>
      )}
      {todayLessons.map((lesson) => {
        const group = groups.find((g) => g.id === lesson.group_id);
        return (
          <div key={lesson.id} className="rounded-xl border border-background-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 flex-col items-center justify-center rounded-lg bg-primary-50 ring-1 ring-primary-100">
                  <span className="font-heading text-sm font-bold text-primary-700">{lesson.start_time.slice(0, 5)}</span>
                  <span className="text-[10px] text-primary-500">{lesson.end_time.slice(0, 5)}</span>
                </div>
                <div>
                  <h4 className="font-heading text-base font-bold text-foreground-950">{lesson.title}</h4>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-600">
                    <span className="flex items-center gap-1">
                      <i className="ri-team-line text-xs" /> {group?.name ?? '-'}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-book-open-line text-xs" /> {lesson.subject}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line text-xs" /> {lesson.type === 'birebir' ? 'Birebir' : 'Grup'}
                    </span>
                  </div>
                </div>
              </div>
              <StatusBadge meta={getStatusMeta(lesson.status, lessonStatusMap)} />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <a
                href={lesson.live_link}
                target="_blank"
                rel="noreferrer nofollow"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600"
              >
                <i className="ri-play-circle-line text-base" /> Dersi Başlat
              </a>
              <button
                type="button"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-white px-4 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-eye-line text-base" /> Detay
              </button>

              <div className="ml-auto flex items-center gap-1.5">
                <button
                  type="button"
                  title="Kayıt Ekle"
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200 hover:text-foreground-900 cursor-pointer"
                >
                  <i className="ri-vidicon-line text-base" />
                </button>
                <button
                  type="button"
                  title="Doküman Ekle"
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-100 text-foreground-600 transition hover:bg-background-200 hover:text-foreground-900 cursor-pointer"
                >
                  <i className="ri-file-pdf-2-line text-base" />
                </button>
                <button
                  type="button"
                  title="Ödev Gönder"
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-800 transition hover:bg-accent-200 cursor-pointer"
                >
                  <i className="ri-task-line text-base" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}